import React, { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';

import { ROLE_ADMIN, useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components';
import { Collaborator } from '@/models';
import { db } from '@/config/firebase';
import { doc, getDoc } from 'firebase/firestore';

type CollaboratorsLayoutContext = {
  collaborators: Collaborator[];
  isLoading: boolean;
  error: Error | null;
};

const formatDate = (value?: any) => {
  if (!value) return '-';
  // Firestore Timestamp support (seconds or _seconds)
  const seconds = value?.seconds ?? value?._seconds;
  if (seconds) {
    const date = new Date(seconds * 1000);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    // Mantém string já formatada (ex: dd/MM/yyyy) quando não for parseável
    if (typeof value === 'string' && value.trim()) return value;
    return '-';
  }
  return date.toLocaleDateString('pt-BR');
};

const formatValue = (value?: string | number | null) => {
  if (value === undefined || value === null) return '-';
  if (typeof value === 'string' && value.trim() === '') return '-';
  return value;
};

const formatPhone = (phone?: string | null) => {
  if (!phone) return '-';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

const buildAddressLine = (address?: any) => {
  if (!address) return '-';
  const primary = [address.street, address.number, address.complement].filter(Boolean).join(', ');
  const location = [address.neighborhood, address.city, address.state].filter(Boolean).join(' - ');
  const zip = address.zipCode;
  const parts = [primary, location, zip].filter((part) => part && String(part).trim().length);
  return parts.length ? parts.join(' · ') : '-';
};

export default function CollaboratorBasicInfoPage() {
  const { collaborators, isLoading, error } = useOutletContext<CollaboratorsLayoutContext>();
  const { id } = useParams<{ id: string }>();
  const { userHasRole } = useAuth();
  const collaborator = useMemo(
    () => collaborators.find((item) => item.id === id),
    [collaborators, id],
  );
  const [detailedCollaborator, setDetailedCollaborator] = useState<Collaborator | null>(collaborator ?? null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);

  const canSeePage = userHasRole(ROLE_ADMIN);

  // Log o registro recebido pelo contexto
  useEffect(() => {
    if (collaborator) {
      // eslint-disable-next-line no-console
      console.log('[CollaboratorBasicInfoPage] collaborator (context)', collaborator);
    }
  }, [collaborator]);

  useEffect(() => {
    let cancelled = false;
    const needsDetails =
      !collaborator?.birthDate ||
      !collaborator?.addresses ||
      !Array.isArray(collaborator.addresses) ||
      collaborator.addresses.length === 0 ||
      !(collaborator as any).personalEmail;

    const fetchDetails = async () => {
      if (!collaborator?.id || !needsDetails) {
        setDetailedCollaborator(collaborator ?? null);
        return;
      }
      setIsDetailsLoading(true);
      try {
        const [publicSnap, privateSnap] = await Promise.all([
          getDoc(doc(db, 'collaborators', collaborator.id)),
          getDoc(doc(db, 'collaborators_private', collaborator.id)).catch(() => null),
        ]);
        const publicData = publicSnap.exists() ? publicSnap.data() : {};
        const privateData = privateSnap && privateSnap.exists() ? privateSnap.data() : {};
        if (!cancelled) {
          const merged = { ...(collaborator as any), ...publicData, ...privateData, id: collaborator.id } as Collaborator;
          // eslint-disable-next-line no-console
          console.log('[CollaboratorBasicInfoPage] merged collaborator', merged);
          setDetailedCollaborator(merged);
        }
      } catch (err) {
        console.error('[CollaboratorBasicInfoPage] Failed to fetch collaborator details', err);
        if (!cancelled) setDetailedCollaborator(collaborator ?? null);
      } finally {
        if (!cancelled) setIsDetailsLoading(false);
      }
    };

    fetchDetails();
    return () => {
      cancelled = true;
    };
  }, [collaborator]);

  // Log final que será renderizado
  useEffect(() => {
    if (detailedCollaborator) {
      // eslint-disable-next-line no-console
      console.log('[CollaboratorBasicInfoPage] detailed collaborator (render)', detailedCollaborator);
    }
  }, [detailedCollaborator]);

  if (!canSeePage) {
    return (
      <div className="rounded-xl border border-amber-100 bg-amber-50 p-6 text-amber-900">
        Você não tem permissão para visualizar os detalhes deste colaborador.
      </div>
    );
  }

  if (isLoading && !collaborators.length) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-red-800">
        Não foi possível carregar o colaborador.
      </div>
    );
  }

  if (!detailedCollaborator) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-gray-700 shadow-sm">
        Colaborador não encontrado.
      </div>
    );
  }

  const mainAddress = detailedCollaborator.addresses?.[0];
  const mailingAddress = detailedCollaborator.addresses?.[1];
  const sections = [
    {
      title: 'Contato',
      items: [
        { label: 'E-mail corporativo', value: formatValue(detailedCollaborator.email) },
        { label: 'E-mail pessoal', value: formatValue((detailedCollaborator as any).personalEmail) },
        { label: 'Telefone', value: formatPhone(detailedCollaborator.phone) },
      ],
    },
    {
      title: 'Dados pessoais',
      items: [
        { label: 'Data de nascimento', value: formatDate((detailedCollaborator as any).birthDate) },
        { label: 'Sexo', value: formatValue((detailedCollaborator as any).sex) },
        { label: 'Raça / Etnia', value: formatValue((detailedCollaborator as any).race) },
        { label: 'Gênero', value: formatValue((detailedCollaborator as any).gender) },
        { label: 'Nacionalidade', value: formatValue((detailedCollaborator as any).nationality) },
        { label: 'Naturalidade (cidade)', value: formatValue((detailedCollaborator as any).naturality) },
        { label: 'Estado de nascimento', value: formatValue((detailedCollaborator as any).birthState) },
      ],
    },
    {
      title: 'Endereço principal',
      items: [
        { label: 'Endereço', value: buildAddressLine(mainAddress) },
        { label: 'Tipo', value: formatValue(mainAddress?.type) },
      ],
    },
    {
      title: 'Endereço de correspondência',
      items: [
        { label: 'Mesmo do principal', value: (detailedCollaborator as any).isMailingSameAsMain ? 'Sim' : 'Não' },
        { label: 'Endereço', value: buildAddressLine(mailingAddress) },
        { label: 'Tipo', value: formatValue(mailingAddress?.type) },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-gradient-to-br from-indigo-50 via-white to-slate-50 p-4 md:p-6 shadow-[0_40px_120px_-60px_rgba(45,55,72,0.35)]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-indigo-700">Dados básicos</p>
            </div>
            <Link
              to={`/collaborators/${detailedCollaborator.id}`}
              className="rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
            >
              Voltar para o perfil
            </Link>
          </div>

          <div className="space-y-4">
            {sections.map((section) => (
              <div
                key={section.title}
                className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                      {section.title}
                    </p>
                  </div>
                </div>
                <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {section.items.map((item) => (
                    <div key={item.label} className="rounded-xl bg-slate-50 px-3 py-2">
                      <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        {item.label}
                      </dt>
                      <dd className="text-sm font-semibold text-slate-900">{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
